import { Client, IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { BASE_API } from '../config/applicationConfig';

/**
 * WebSocket service for handling WebSocket connections and messages.
 * This service is used to subscribe to real-time updates from the backend.
 */
class WebSocketService {
  private client: Client | null = null;
  private subscriptions: Map<string, (data: any) => void> = new Map();
  private connected = false;

  /**
   * Initialize the WebSocket connection.
   * This method should be called when the application starts.
   */
  public init(): void {
    this.client = new Client({
      webSocketFactory: () => new SockJS(`/api/ws`),
      onConnect: this.onConnect.bind(this),
      onDisconnect: this.onDisconnect.bind(this),
      onStompError: this.onError.bind(this),
      reconnectDelay: 5000,
    });

    this.client.activate();
  }

  /**
   * Subscribe to a topic.
   * @param topic The topic to subscribe to
   * @param callback The callback function to call when a message is received
   * @returns A function to unsubscribe from the topic
   */
  public subscribe<T>(topic: string, callback: (data: T) => void): () => void {
    this.subscriptions.set(topic, callback);

    if (this.connected && this.client) {
      const subscription = this.client.subscribe(topic, (message: IMessage) => {
        const data = JSON.parse(message.body);
        callback(data);
      });

      return () => {
        subscription.unsubscribe();
        this.subscriptions.delete(topic);
      };
    }

    return () => {
      this.subscriptions.delete(topic);
    };
  }

  /**
   * Disconnect the WebSocket connection.
   * This method should be called when the application stops.
   */
  public disconnect(): void {
    if (this.client) {
      this.client.deactivate();
      this.client = null;
      this.connected = false;
    }
  }

  /**
   * Called when the WebSocket connection is established.
   */
  private onConnect(): void {
    console.log('WebSocket connected');
    this.connected = true;

    // Subscribe to all topics
    if (this.client) {
      this.subscriptions.forEach((callback, topic) => {
        this.client!.subscribe(topic, (message: IMessage) => {
          const data = JSON.parse(message.body);
          callback(data);
        });
      });
    }
  }

  /**
   * Called when the WebSocket connection is closed.
   */
  private onDisconnect(): void {
    console.log('WebSocket disconnected');
    this.connected = false;
  }

  /**
   * Called when a WebSocket error occurs.
   * @param frame The error frame
   */
  private onError(frame: any): void {
    console.error('WebSocket error', frame);
  }
}

// Create a singleton instance of the WebSocketService
const webSocketService = new WebSocketService();

export default webSocketService;
